// src/components/AccountsReport.jsx
import React, { useEffect, useState } from "react";
import axios from "axios";
import PropTypes from "prop-types";
import AppConst from "../apis/appconst";
import Swal from "sweetalert2";


const pad = (n) => String(n).padStart(2, "0");
const monthsShort = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];

function nowYMD() {
  const d = new Date();
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}
function nowHHMM() {
  const d = new Date();
  return `${pad(d.getHours())}:${pad(d.getMinutes())}`;
}
function nowHHMMSS() {
  const d = new Date();
  return `${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
}
function formatDatePrettyYMD(ymd) {
  if (!ymd) return "";
  const [y,m,d] = String(ymd).split("-");
  const mon = monthsShort[Number(m) - 1] || m;
  return `${pad(Number(d))}-${mon}-${y}`;
}
function formatTime12(hhmm) {
  if (!hhmm) return "";
  const parts = String(hhmm).split(":");
  let hr = Number(parts[0] || 0);
  const mn = pad(Number(parts[1] || 0));
  const ampm = hr >= 12 ? "PM" : "AM";
  hr = hr % 12 || 12;
  return `${pad(hr)}:${mn} ${ampm}`;
}

function classifyMode(raw = "") {
  const s = String(raw || "").toLowerCase();
  if (!s) return "";
  if (s.includes("cash")) return "Cash";
  if (s.includes("card") || s.includes("debit") || s.includes("credit") || s.includes("swipe")) return "Card";
  if (s.includes("upi") || s.includes("online") || s.includes("gateway") || s.includes("net")) return "Online";
  return "";
}

// helper already exists above; re-use it. Add this adjuster near your setTotalsCallback declaration:
const adjustTotalsExcludingDue = (t = {}) => {
  const getNum = (klist) => {
    for (let k of klist) {
      if (Object.prototype.hasOwnProperty.call(t, k)) {
        const n = Number(t[k]);
        return isNaN(n) ? 0 : n;
      }
    }
    return 0;
  };

  // try a bunch of possible keys for total and due
  const totalKeys = ['total','sum_total','lab_final_amt','opd_total','med_bill_final_amt','totalAmount','sum_reg_charges','sum_ipd_bill'];
  const dueKeys   = ['due','sum_due','lab_due','opd_due','med_bill_due','sum_reg_charges_due','reg_charges_due','ipd_bill_total_due'];

  const total = getNum(totalKeys);
  const due   = getNum(dueKeys);

  const adjustedTotal = Math.max(0, total - due);

  // return shallow copy with adjusted total (preserve original keys)
  return { ...t, total: adjustedTotal };
};
// Add this new function in AccountsReport.jsx (after inferRowPaymentSplits)
function extractProcedureBillSplits(row) {
  if (!row || typeof row !== "object") return { cash: 0, card: 0, online: 0 };

  const cash = parseFloat(row?.procedure_bill_cash || 0);
  const card = parseFloat(row?.procedure_bill_card || 0);
  const online = parseFloat(row?.procedure_bill_online || 0);

  return { cash, card, online };
}
// ✅ Helper: infer payment mode and split amounts (cash/card/online)
// ✅ Helper: infer payment mode and split amounts (cash/card/online)
// now takes paidKey so it uses the actual paid field (safer)
function inferRowPaymentSplits(row, paidKey = "paid") {
  if (!row || typeof row !== "object") return { cash: 0, card: 0, online: 0 };

  let cash = 0, card = 0, online = 0;

  // 1) Determine the explicit paid amount using the paidKey first, then safe fallbacks.
  const safePaidCandidates = [
    paidKey,
    "paid", "paid_amount", "lab_paid", "paidAmount", "opd_paid",
    "amount_paid", "amountPaid", "paid_total", "paidTotal", "payment_amount"
  ];
  let paidAmount = 0;
  for (const k of safePaidCandidates) {
    if (Object.prototype.hasOwnProperty.call(row, k)) {
      const v = parseFloat(row[k]);
      if (isFinite(v) && v > 0) { paidAmount = v; break; }
    }
  }

  // 2) Try explicit paid_* fields (these are the highest-confidence splits).
  const explicitCash = parseFloat(row?.paid_cash || row?.paidCash || row?.paid_amount_cash || row?.paid_amount_cash_paid) || 0;
  const explicitCard = parseFloat(row?.paid_card || row?.paidCard || row?.paid_amount_card) || 0;
  const explicitOnline = parseFloat(row?.paid_online || row?.paidOnline || row?.paid_amount_online) || 0;

  const explicitSum = (explicitCash || 0) + (explicitCard || 0) + (explicitOnline || 0);
  if (explicitSum > 0) {
    // trust explicit split fields when present (they represent actual paid sums)
    cash += explicitCash;
    card += explicitCard;
    online += explicitOnline;
    return { cash, card, online };
  }

  // 3) Detect a payment-mode value from common fields
  const possibleModeFields = [
    "opd_mode", "opd_bill_pay_mode", "ipd_bill_pay_mode",
    "payment_mode", "pay_mode", "pay_type", "payment_type",
    "lab_pay_mode", "lab_due_pay_mode", "lab_reg_pay_mode",
    "med_bill_pay_mode", "mode"
  ];
  let payModeVal = "";
  for (const f of possibleModeFields) {
    if (Object.prototype.hasOwnProperty.call(row, f) && row[f]) {
      payModeVal = String(row[f]);
      break;
    }
  }
  if (!payModeVal) {
    // also look for any cell contents that explicitly mention cash/card/upi/online
    for (const k of Object.keys(row)) {
      const val = row[k];
      if (!val) continue;
      const s = String(val).toLowerCase();
      if (s.includes("cash") || s.includes("card") || s.includes("upi") || s.includes("online") || s.includes("gateway") || s.includes("net")) {
        payModeVal = String(val);
        break;
      }
    }
  }

  // 4) If we still don't have a paidAmount, do a conservative numeric-scan,
  // but restrict to keys that strongly indicate a paid value (avoid 'total'/'due').
  if (!paidAmount) {
    for (const k of Object.keys(row)) {
      if (!/paid|amount|payment/i.test(k) || /due|discount|balance/i.test(k)) continue;
      const maybe = parseFloat(row[k]);
      if (isFinite(maybe) && maybe > 0) { paidAmount = maybe; break; }
    }
  }

  // 5) Allocate paidAmount according to mode (or default to cash)
  const payStr = (payModeVal || "").toLowerCase();
  if (paidAmount > 0) {
    if (payStr.includes("cash")) cash += paidAmount;
    else if (payStr.includes("card") || payStr.includes("debit") || payStr.includes("credit") || payStr.includes("swipe")) card += paidAmount;
    else if (payStr.includes("upi") || payStr.includes("online") || payStr.includes("gateway") || payStr.includes("net")) online += paidAmount;
    else cash += paidAmount;
    return { cash, card, online };
  }

  // 6) final fallback: if nothing at all matches, return zeros (do not attribute totals/due)
  return { cash: 0, card: 0, online: 0 };
}


// ✅ Emergency / DayCare fallback:
// If procedure bill exists BUT no payment split detected,
// treat it as CASH paid (once only)



export default function AccountsReport({
  title = "Accounts Report",
  apiUrl,
  method = "auto",
  fetchParams = {},
  mapping = {},
  onTotalsChange = () => {},
  compact = false,
  extraAxiosConfig = {},
  collectApi = "Webapis/collect_account",
  collectHistoryApi = "Webapis/get_collections",
  currentUserId = null,
  currentUserName = null,
  usePaymentTransactions = false,
  paymentTransactionsApi = null,
  paymentTransactionKey = 'ipd_uhid',
  extraSums = {},
  overridePaymentBreakdown = null // <-- new prop: { "Label": "field_name", ... }
}) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [totals, setTotals] = useState({ total:0, paid:0, due:0, rows:0, cash:0, card:0, online:0, discount:0 });
  const [error, setError] = useState(null);
  const [rawResponse, setRawResponse] = useState(null);

  const [showCollect, setShowCollect] = useState(false);
  const [collectAmount, setCollectAmount] = useState("");
  const [collectDate, setCollectDate] = useState(nowYMD());
  const [collectTime, setCollectTime] = useState(nowHHMM());
  const [collectUserId, setCollectUserId] = useState("");
  const [collectUserName, setCollectUserName] = useState("");
  const [collectNote, setCollectNote] = useState("");
  const [collectSubmitting, setCollectSubmitting] = useState(false);

  const [usersList, setUsersList] = useState([]);
  const [usersLoading, setUsersLoading] = useState(false);

  const [collectionHistory, setCollectionHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);

  const resolveFullUrl = (u) => {
    if (!u) return u;
    if (/^https?:\/\//.test(u)) return u;
    const rel = AppConst[u] || u;
    return `${AppConst.BASE_URL.replace(/\/+$/,"")}/${String(rel).replace(/^\/+/,"")}`;
  };

  const makeFormData = (obj) => {
    if (obj instanceof FormData) return obj;
    const fd = new FormData();
    Object.entries(obj || {}).forEach(([k,v]) => {
      if (v === undefined || v === null) return;
      if (Array.isArray(v)) v.forEach(item => fd.append(k + "[]", item));
      else fd.append(k, v);
    });
    return fd;
  };

  const detectLoggedIn = () => {
    if (currentUserId && currentUserName) return { id: String(currentUserId), name: String(currentUserName) };
    try {
      const cur = localStorage.getItem("currentUser") || localStorage.getItem("user");
      if (cur) {
        const parsed = JSON.parse(cur);
        const id = parsed?.user_id ?? parsed?.id ?? parsed?.uid ?? parsed?.userId ?? null;
        const name = parsed?.fname || parsed?.name || parsed?.user_display || parsed?.username || "";
        if (id) return { id: String(id), name: String(name) };
      }
    } catch {}
    return null;
  };

  // -------------------- LOAD REPORT --------------------
  useEffect(() => {
    if (!apiUrl) {
      const zero = { total:0, paid:0, due:0, rows:0, cash:0, card:0, online:0, discount:0 };
      setRows([]); setTotals(zero); onTotalsChange(zero);
      setError(new Error("No apiUrl provided"));
      return;
    }

    const source = axios.CancelToken.source();
    let mounted = true;

    const load = async () => {
      setLoading(true);
      setError(null);
      setRawResponse(null);

      const resolvedUrl = resolveFullUrl(apiUrl);

      try {
        let res;
        const body = makeFormData(fetchParams);
        try {
          res = await axios.post(resolvedUrl, body);
        } catch (err) {
          const text = JSON.stringify(err?.response?.data || "");
          if (String(text).includes("Cannot POST")) res = await axios.get(resolvedUrl, { params: fetchParams });
          else throw err;
        }

        if (!mounted) return;
        setRawResponse(res.data);

        // ✅ FIXED SERVER TOTALS BLOCK (seed only)
       // --- unified totals parser (handles backend-supplied totals) ---
// ✅ unified totals parser (handles backend-supplied totals)
const serverTotals = res.data?.totals;
let usedServerTotals = false;

if (serverTotals && typeof serverTotals === 'object') {
  const newTotals = {
    total:    Number(serverTotals.sum_ipd_bill ?? serverTotals.sum_total_amount ?? serverTotals.total ?? 0),
    paid:     Number(serverTotals.sum_paid ?? serverTotals.sum_total_paid ?? serverTotals.paid ?? 0),
    due:      Number(serverTotals.sum_due ?? serverTotals.total_due ?? serverTotals.due ?? 0),
    rows:     Number(serverTotals.count ?? 0),
    cash:     Number(serverTotals.total_cash ?? serverTotals.sum_paid_cash ?? serverTotals.paid_cash ?? 0),
    card:     Number(serverTotals.total_card ?? serverTotals.sum_paid_card ?? serverTotals.paid_card ?? 0),
    online:   Number(serverTotals.total_online ?? serverTotals.sum_paid_online ?? serverTotals.paid_online ?? 0),
    discount: Number(serverTotals.total_discount ?? serverTotals.discount ?? 0),
    total_return_amount: Number(serverTotals.total_return_amount ?? 0),
    final_collection: Math.max(0, Number(serverTotals.final_collection ?? 0))
  };




  
  // ✅ if backend already gives per-method totals, use them directly
  const hasMethodBreakdown =
    (newTotals.cash > 0 || newTotals.card > 0 || newTotals.online > 0);

  if (hasMethodBreakdown) {
    setTotals(newTotals);
    const payload = {
      ...newTotals,
      ...serverTotals,
      _server: serverTotals,
    };
    onTotalsChange(payload);
    usedServerTotals = true;
  }
}

// ✅ now safely process rows AFTER defining items
const data = res.data ?? {};
const items = Array.isArray(data.items) ? data.items
  : Array.isArray(data.data) ? data.data
  : Array.isArray(data.records) ? data.records
  : Array.isArray(data.rows) ? data.rows
  : Array.isArray(data) ? data : [];
setRows(items);

// compute totals and breakdowns (only if backend didn’t give them)
// Find this section in AccountsReport.jsx and update it:
if (!usedServerTotals) {
  const totalKey = mapping.total || "total";
  const paidKey  = mapping.paid  || "paid";
  const dueKey   = mapping.due   || "due";
  const discountKeys = [mapping.discount, "discount", "ipd_discount", "discount_amt"].filter(Boolean);

  const extraSumsMap = extraSums || {};
  const extraSumsAcc = {};
  for (const label of Object.keys(extraSumsMap)) extraSumsAcc[label] = 0;

  let total = 0, paid = 0, due = 0, cash = 0, card = 0, online = 0, discount = 0;
  let unknownAcc = 0;
  
  // ✅ NEW: Add procedure bill payment breakdown accumulators
  let procedureCash = 0, procedureCard = 0, procedureOnline = 0;
  const isEmergency = title === "Emergency / DayCare Accounts";
  for (const r of items) {
    const t = parseFloat(r?.[totalKey]) || 0;
    


    const p = parseFloat(r?.[paidKey]) || 0;

    const d = typeof r?.[dueKey] !== "undefined" ? parseFloat(r?.[dueKey]) || 0 : (t - p);
    total += t;
    paid += p;
    due += d;
    discount += parseFloat(r?.discount_amt || r?.discount || 0) || 0;

    const splits = inferRowPaymentSplits(r, paidKey);
    cash += splits.cash || 0;
    card += splits.card || 0;
    online += splits.online || 0;
    unknownAcc += splits.unknown || 0;


   // ✅ Emergency / DayCare — add procedure bills ONCE (already summed safely)


    
    // ✅ NEW: Extract procedure bill payment splits
   

    // accumulate extras
    for (const [label, fieldName] of Object.entries(extraSumsMap)) {
      const v = parseFloat(r?.[fieldName]) || 0;
      extraSumsAcc[label] += v;
    }
  }

  // ✅ NEW: Add procedure bill breakdowns to cash/card/online

 // ✅ Emergency / DayCare: Procedure bills are CASH collected


  const newTotals = {
    total,
    paid,
    due,
    rows: items.length,
    cash,
    card,
    online,
    unknown: unknownAcc,
    discount,
    extra: extraSumsAcc,
    // ✅ NEW: Add procedure-specific breakdowns for debugging
    procedureBillBreakdown: {
      cash: procedureCash,
      card: procedureCard,
      online: procedureOnline,
      total: procedureCash + procedureCard + procedureOnline
    }
  };

  setTotals(newTotals);
  onTotalsChange(newTotals);
}


      } catch (err) {
        console.error("[AccountsReport] error", err);
        const zero = { total:0, paid:0, due:0, rows:0, cash:0, card:0, online:0, discount:0 };
        setRows([]); setTotals(zero); onTotalsChange(zero);
      } finally {
        if (mounted) setLoading(false);
      }
    };

    load();
    return () => { mounted = false; source.cancel(); };
  }, [apiUrl, JSON.stringify(fetchParams), method]);


  // ✅ Emergency / DayCare:
// Procedure bills are PAID, but payment mode stays as-is (cash already counted)


  // ---------- Render ----------
 // In AccountsReport.jsx, update the render section (around line 200+):

 return (
  <div className="card h-100">
    <div className="card-body">
      <div className="d-flex justify-content-between align-items-start mb-2">
        <div>
          <h6 className="card-title mb-1">{title}</h6>
          <small className="text-muted">{compact ? "Compact" : "Full"}</small>
        </div>
      </div>

      {loading ? (
        <div className="mt-2">Loading…</div>
      ) : (
        <>
          {/* ✅ Main Metrics */}
          <div className="mt-2 d-flex flex-column gap-2">
            <div>
              <small className="text-muted">Total</small>
              <div><strong style={{ fontSize: '1.1rem' }}>₹{totals.total.toFixed(2)}</strong></div>
            </div>
            <div>
              <small className="text-muted">Paid</small>
              <div><strong style={{ fontSize: '1.1rem' }}>₹{totals.paid.toFixed(2)}</strong></div>
            </div>
            <div>
              <small className="text-muted">Due</small>
              <div><strong style={{ fontSize: '1.1rem' }}>₹{totals.due.toFixed(2)}</strong></div>
            </div>

            <div className="mt-1">
              <small className="text-muted">Total Patients</small>
              <div><strong>{totals.rows}</strong></div>
            </div>
          </div>

          {/* ✅ Payment Breakdown */}
         {/* ✅ Payment Breakdown */}
<div className="mt-3 pt-2 border-top">
  <small className="text-muted d-block mb-2">Payment Breakdown</small>
  <div className="d-flex gap-3 flex-wrap">
    <div><small>Cash</small><div><strong>₹{(overridePaymentBreakdown?.cash ?? totals.cash).toFixed(2)}</strong></div></div>
    <div><small>Card</small><div><strong>₹{(overridePaymentBreakdown?.card ?? totals.card).toFixed(2)}</strong></div></div>
    <div><small>Online</small><div><strong>₹{(overridePaymentBreakdown?.online ?? totals.online).toFixed(2)}</strong></div></div>
    {totals.discount > 0 && (
      <div><small>Discount</small><div><strong>₹{totals.discount.toFixed(2)}</strong></div></div>
    )}
  </div>
</div>

          {/* ✅ NEW: Return Amount & Final Collection (Pharmacy only) */}
          {title.toLowerCase().includes('pharmacy') && (
            <div className="mt-3 pt-2 border-top">
              <small className="text-muted d-block mb-2">Return & Collection</small>
              <div className="d-flex gap-3 flex-wrap">
                {/* Return Amount */}
                {totals.total_return_amount !== undefined && (
                  <div>
                    <small className="text-danger">Return Amount</small>
                    <div>
                      <strong className="text-danger">
                        ₹{Number(totals.total_return_amount || 0).toFixed(2)}
                      </strong>
                    </div>
                  </div>
                )}
                
                {/* Final Collection */}
                {totals.final_collection !== undefined && (
                  <div>
                    <small className="text-success">Final Collection</small>
                    <div>
                      <strong className="text-success" style={{ fontSize: '1.15rem' }}>
                        ₹{Number(totals.final_collection || 0).toFixed(2)}
                      </strong>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* ✅ Procedure Bills & Other Details */}
          {totals.extra && Object.keys(totals.extra).length > 0 && (
            <div className="mt-3 pt-2 border-top">
              <small className="text-muted d-block mb-2">Other Details</small>
              <div className="d-flex gap-3 flex-wrap">
                {Object.keys(totals.extra).map((label) => (
                  <div key={label}>
                    <small>{label}</small>
                    <div><strong>₹{Number(totals.extra[label] || 0).toFixed(2)}</strong></div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  </div>
);
}

AccountsReport.propTypes = {
  title: PropTypes.string,
  apiUrl: PropTypes.string.isRequired,
  method: PropTypes.oneOf(["auto", "get", "post"]),
  fetchParams: PropTypes.oneOfType([PropTypes.object, PropTypes.instanceOf(FormData)]),
  mapping: PropTypes.object,
  onTotalsChange: PropTypes.func,
  compact: PropTypes.bool,
  extraAxiosConfig: PropTypes.object,
  collectApi: PropTypes.string,
  collectHistoryApi: PropTypes.string,
  currentUserId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  currentUserName: PropTypes.string,
  extraSums: PropTypes.object,
  overridePaymentBreakdown: PropTypes.object,  // ✅ ADD THIS
};
